import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useSearch } from '@/src/contexts/SearchContext';
import { FeatureType, LandingLoginSource } from '@/src/types';
import Launchpad from '@/src/components/Launchpad';
import DiscoveryHub from '@/src/components/DiscoveryHub';
import OptimizeHub from '@/src/components/OptimizeHub';
import ScrutinyHub from '@/src/components/ScrutinyHub';
import { CURRENCIES } from '@/src/constants';

interface InteractiveHeroPreviewProps {
  activeCurrency: { code: string; name: string; symbol: string } | string;
  filteredCurrencies: ({ code: string; name: string; symbol: string } | string)[];
  exchangeRates: Record<string, number>;
  currentMinBudget: number;
  currentMaxBudget: number;
  launchpadLocRef: React.RefObject<HTMLInputElement | null>;
  discLocRef: React.RefObject<HTMLInputElement | null>;
  filteredCountries: string[];
  user: any;
  userProfile: any;
  isLoggedIn: boolean;
  onOpenUpgradeModal: () => void;
  onOpenSignUpModal: (source?: LandingLoginSource) => void;
  executePaidAction: (action: () => void) => void;
  setIsScrutinyActive: (active: boolean) => void;
  onOpenSavedOpportunities?: () => void;
  savedCount?: number;
}

type HubId = 'launchpad' | 'discovery' | 'optimize' | 'scrutiny';

export const InteractiveHeroPreview: React.FC<InteractiveHeroPreviewProps> = ({
  activeCurrency,
  filteredCurrencies,
  exchangeRates,
  currentMinBudget,
  currentMaxBudget,
  launchpadLocRef,
  discLocRef,
  filteredCountries,
  user,
  userProfile,
  isLoggedIn,
  onOpenUpgradeModal,
  onOpenSignUpModal,
  executePaidAction,
  setIsScrutinyActive,
  onOpenSavedOpportunities,
  savedCount = 0
}) => {
  const [activeHub, setActiveHub] = useState<HubId>('launchpad');
  const { dispatch } = useSearch();
  const workspaceRef = useRef<HTMLDivElement>(null);

  const handleHubSelect = (hubId: HubId) => {
    setActiveHub(hubId);

    // Smoothly scroll up / peek up the modal for the selected card
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (workspaceRef.current) {
          const headerOffset = 88;
          const rect = workspaceRef.current.getBoundingClientRect();
          const targetY = Math.max(0, window.scrollY + rect.top - headerOffset);

          window.scrollTo({
            top: targetY,
            behavior: 'smooth'
          });
        }
      }, 40);
    });
  };

  const currencyObj = useMemo(() => {
    if (activeCurrency && typeof activeCurrency === 'object' && 'code' in activeCurrency) {
      return activeCurrency as { code: string; name: string; symbol: string };
    }
    const code = typeof activeCurrency === 'string' ? activeCurrency : 'USD';
    const found = CURRENCIES.find(c => c.code === code);
    return found || { code, name: 'US Dollar', symbol: '$' };
  }, [activeCurrency]);

  const currencyList = useMemo(() => {
    if (Array.isArray(filteredCurrencies) && filteredCurrencies.length > 0) {
      if (typeof filteredCurrencies[0] === 'object' && filteredCurrencies[0] !== null) {
        return filteredCurrencies as { code: string; name: string; symbol: string }[];
      }
      return CURRENCIES.filter(c => (filteredCurrencies as string[]).includes(c.code));
    }
    return CURRENCIES;
  }, [filteredCurrencies]);

  const hubs = [
    {
      id: 'launchpad' as HubId,
      letter: 'L',
      name: 'Launchpad',
      description:
        'Validate new ideas. Enter your concept, location, sector, and budget. Get startup costs, break-even timeline, risk scores, and exact site recommendations.'
    },
    {
      id: 'discovery' as HubId,
      letter: 'D',
      name: 'Discovery Hub',
      description:
        'Explore any area. Uncover what neighborhoods are actually known for. View business density heatmaps. Verify real competitors with live data.'
    },
    {
      id: 'optimize' as HubId,
      letter: 'O',
      name: 'Optimize',
      description:
        'Scale what works. Connect your active business. Track actuals against original projections. Model expansions, pricing changes, and new locations.'
    },
    {
      id: 'scrutiny' as HubId,
      letter: 'S',
      name: 'Scrutiny Deck',
      description:
        'Stress-test assumptions. Submit any raw idea. Receive brutal, data-backed analysis of fatal gaps, hidden assumptions, and viable pivot paths.'
    }
  ];

  return (
    <section className="w-full space-y-12 md:space-y-16">
      {/* HERO SECTION - Full-width dark layout */}
      <div className="space-y-6 max-w-4xl text-left">
        <h1 className="text-[32px] md:text-[36px] font-medium text-[#FFFFFF] tracking-tight leading-tight">
          Hyperlocal Venture Intelligence
        </h1>

        <p className="text-base md:text-lg text-white/60 max-w-2xl font-normal leading-relaxed">
          Precision venture intelligence from first idea to live operation. Discover where. Validate how. Scale with certainty...
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            onClick={() => onOpenSignUpModal('hero_start_free')}
            className="bg-[#F26522] hover:bg-[#F26522]/90 text-[#FFFFFF] font-medium text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer flex items-center gap-2"
          >
            <span>Start free →</span>
          </button>

          <button
            onClick={() => {
              const el = document.getElementById('how-to-use-section');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-transparent text-[#FFFFFF] border border-white/20 hover:bg-white/5 font-medium text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer"
          >
            How to use
          </button>

          {onOpenSavedOpportunities && (
            <button
              onClick={onOpenSavedOpportunities}
              id="hero-access-saved-btn"
              className="bg-[#3a3430] hover:bg-white/10 text-[#FFFFFF] border border-white/20 hover:border-[#F26522]/50 font-medium text-sm px-5 py-3 rounded-lg transition-all cursor-pointer flex items-center gap-2.5 group"
            >
              <i className="fas fa-bookmark text-[#F26522] text-xs transition-transform group-hover:scale-110"></i>
              <span>Access Saved Opportunities</span>
              {savedCount > 0 && (
                <span className="bg-[#F26522] text-white text-[10px] font-bold px-2 py-0.5 rounded-full leading-none">
                  {savedCount}
                </span>
              )}
            </button>
          )}
        </div>

        <div className="pt-2">
          <p className="text-xs text-white/60 tracking-normal font-normal">
            No credit card needed
          </p>
        </div>
      </div>

      {/* SAVED OPPORTUNITIES DEDICATED CALLOUT */}
      {savedCount > 0 && onOpenSavedOpportunities && (
        <div 
          onClick={onOpenSavedOpportunities}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenSavedOpportunities();
            }
          }}
          className="w-full bg-[#3a3430] hover:bg-[#3a3430]/80 border border-[#F26522]/30 hover:border-[#F26522]/60 rounded-xl p-5 md:p-6 transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 group text-left"
          id="saved-opportunities-landing-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#F26522]/15 border border-[#F26522]/30 flex items-center justify-center text-[#F26522] shrink-0 transition-transform group-hover:scale-105">
              <i className="fas fa-bookmark text-base"></i>
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <span className="text-sm md:text-base font-bold text-white uppercase tracking-tight font-heading">
                  Access Saved Opportunities
                </span>
                <span className="bg-[#F26522] text-white text-[10px] font-bold px-2 py-0.5 rounded-full leading-none">
                  {savedCount} {savedCount === 1 ? 'Opportunity' : 'Opportunities'}
                </span>
              </div>
              <p className="text-xs md:text-sm text-white/60 mt-1">
                Jump straight to your bookmarked ventures, compare viability side-by-side, or export dossiers.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#F26522] shrink-0 font-heading">
            <span>Open Archive</span>
            <i className="fas fa-arrow-right text-[10px] transition-transform group-hover:translate-x-1"></i>
          </div>
        </div>
      )}

      {/* THE FOUR HUBS SECTION */}
      <div className="space-y-6 text-left">
        <h2 className="text-xs uppercase tracking-[0.2em] text-white/60 font-medium">
          The four hubs
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {hubs.map((hub) => {
            const isSelected = activeHub === hub.id;
            return (
              <div
                key={hub.id}
                id={`hub-card-${hub.id}`}
                role="button"
                tabIndex={0}
                onClick={() => handleHubSelect(hub.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleHubSelect(hub.id);
                  }
                }}
                className={`bg-[#3a3430] border rounded-xl p-6 lg:p-8 flex flex-col justify-between transition-all cursor-pointer text-left group select-none ${
                  isSelected 
                    ? 'border-[#F26522]/50 ring-1 ring-[#F26522]/40' 
                    : 'border-white/[0.06] hover:border-white/[0.14]'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-3.5">
                    <div className="w-8 h-8 rounded bg-[#F26522] flex items-center justify-center text-white font-bold text-sm shrink-0 select-none transition-all duration-200 group-hover:scale-105 group-hover:brightness-110 group-hover:ring-1 group-hover:ring-[#F26522]/80">
                      {hub.letter}
                    </div>
                    <h3 className="text-white font-medium text-lg tracking-tight">
                      {hub.name}
                    </h3>
                  </div>

                  <p className="text-xs md:text-sm text-white/60 leading-relaxed font-normal">
                    {hub.description}
                  </p>
                </div>

                <div className="pt-5 flex items-center justify-between text-[11px] text-white/40 border-t border-white/[0.04] mt-5">
                  <span className="uppercase tracking-wider font-mono">
                    {isSelected ? 'Active Workspace' : 'Click to launch'}
                  </span>
                  <span className={isSelected ? 'text-[#F26522] font-medium' : 'text-white/40'}>
                    {isSelected ? '● Running' : '→'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* LIVE INTERACTIVE HUB WORKSPACE */}
      <div 
        ref={workspaceRef} 
        id="active-hub-workspace" 
        className="pt-8 border-t border-white/[0.06] scroll-mt-24"
      >
        <div className="mb-6 flex items-center justify-between text-left">
          <div>
            <h3 className="text-sm font-medium uppercase tracking-wider text-white">
              Active Hub: <span className="text-[#F26522]">{hubs.find(h => h.id === activeHub)?.name}</span>
            </h3>
            <p className="text-xs text-white/60 mt-0.5">
              Configure parameters and run live venture intelligence reconnaissance
            </p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeHub}
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-xl"
            id={`active-hub-modal-${activeHub}`}
          >
            {activeHub === 'launchpad' && (
              <Launchpad
                activeCurrency={currencyObj}
                filteredCurrencies={currencyList}
                exchangeRates={exchangeRates}
                currentMinBudget={currentMinBudget}
                currentMaxBudget={currentMaxBudget}
                launchpadLocRef={launchpadLocRef}
                user={user}
              />
            )}

            {activeHub === 'discovery' && (
              <DiscoveryHub
                discLocRef={discLocRef}
                filteredCountries={filteredCountries}
              />
            )}

            {activeHub === 'optimize' && (
              <div className="max-w-3xl mx-auto">
                <OptimizeHub
                  onConnect={() => {
                    if (!isLoggedIn) {
                      onOpenSignUpModal('optimize');
                      return;
                    }
                    const isOptimizeOrAbove = ['optimize', 'team', 'enterprise'].includes(userProfile?.subscriptionTier);
                    if (!isOptimizeOrAbove) {
                      onOpenUpgradeModal();
                      return;
                    }
                    dispatch({ type: 'SET_ACTIVE_FEATURE', payload: FeatureType.Optimize });
                  }}
                />
              </div>
            )}

            {activeHub === 'scrutiny' && (
              <div className="max-w-3xl mx-auto">
                <ScrutinyHub
                  onOpen={() => {
                    if (!isLoggedIn) {
                      onOpenSignUpModal('scrutiny');
                      return;
                    }
                    setIsScrutinyActive(true);
                  }}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default InteractiveHeroPreview;
